<script setup lang="ts">
import HeaderBranding from "@/components/io/io/header/HeaderBranding.vue";
import MenuTrigger from "@/components/io/io/flyoutmenu/MenuTrigger.vue";
import Branding from "@/components/io/io/Branding.vue";
import FlyoutMenu from "@/components/io/io/flyoutmenu/FlyoutMenu.vue";
import FlyoutMenuItem from "@/components/io/io/flyoutmenu/FlyoutMenuItem.vue";
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { companyName, headerContainerHeight, headerContainerHeightFixed } from '@/io'

const isMounted = useMounted()
const { $io } = useNuxtApp()

const headerFixed = computed(() => $io.headerFixed)
const mobileMenuActive = computed(() => $io.mobileMenuActive)

const router = useRouter()
const route = useRoute()

// Expecting menu items to be passed in; fallback default shown for reference.
const props = withDefaults(defineProps<{
  menu: Array<MenuItemSettings>
}>(), {
  menu: () => [{
    label: 'Home',
    type: 'route',
    name: 'index',
    routePath: '/',
  }],
})

// Track which parent menu is expanded (if any)
const expandedParent = ref<string | null>(null)

// Toggle the mobile nav flyout active state if needed.
function handleToggle() {
  $io.mobileNavFlyoutActive = !$io.mobileNavFlyoutActive
}

// Handle clicks on a parent menu item.
// If the item has children, toggle its expansion.
// Otherwise, navigate (or perform an action) and close the flyout.
function handleItemClick(item: MenuItemSettings, close: () => void) {
  if (item.children && item.children.length > 0) {
    // Toggle the expanded state for submenu animation.
    expandedParent.value = expandedParent.value === item.name ? null : item.name
  } else if (item.type === 'route' && item.routePath) {
    router.push(item.routePath)
    close() // close flyout immediately after navigation
  } else if (item.type === 'action' && 'action' in item) {
    // Execute the action then close the flyout.
    alert("Attach menu actions")
    close()
  }
}

// Handle child menu item click: simply navigate and close.
function handleChildClick(item: MenuItemSettings, close: () => void) {
  if (item.routePath) {
    router.push(item.routePath)
    close()
  }
}

// Active state helper: checks if a menu item is active
function isActive(item: MenuItemSettings): boolean {
  if (item.routePath === route.path || item.name === route.name) {
    return true;
  }
  if (item.children) {
    return item.children.some(child => child.name === route.name || child.routePath === route.path);
  }
  return false;
}

// Active state for child items.
function isChildActive(child: MenuItemSettings): boolean {
  return child.name === route.name || child.routePath === route.path;
}
</script>

<template>
  <Section v-if="isMounted && mobileMenuActive" class="sec-mobile-navigation" w="grid grid-cols-[1fr_60px] mw-92 mx-auto">
    <Column class="sec-branding" w="w-full h-full">
      <HeaderBranding />
    </Column>
    <Column class="sec-response-toggle relative z-10">
      <Popover v-if="isMounted" ref="popover" v-slot="{ open, close }" class="sec-flyout-popover relative">
        <PopoverButton class="btn-flyout-trigger" :class="{ active: open }">
          <MenuTrigger :active="open" :fixed="headerFixed" @toggle="handleToggle" />
        </PopoverButton>

        <transition-slide :offset="['-100%', 0]">
          <PopoverPanel class="sec-flyout-menu" :class="{ active: open }">
            <div class="sec-flyout-menu-inner">
              <!-- Use the headlessui close function directly -->
              <button class="btn-close" @click="close">
                <slot name="close-btn-icon">
                  <i class="flex i i-mdi:close" />
                </slot>
              </button>
              <div class="sec-navigation-wrapper">
                <div class="sec-flyout-header">
                  <slot name="flyoutheader">
                    <Branding />
                  </slot>
                </div>
                <div class="sec-flyout-container">
                  <slot name="flyoutprimary">
                    <FlyoutMenu>
                      <!-- Loop through primary items -->
                      <div v-for="item in props.menu" :key="item.name">
                        <FlyoutMenuItem
                          :label="item.label"
                          :slug="item.type === 'route' ? item.name : ''"
                          :name="item.name"
                          :parent="!!item.children"
                          :class="{ active: isActive(item) }"
                          @click="handleItemClick(item, close)"
                          :active="isActive(item)"
                        >
                          <!-- If the item has children, animate them with a transition -->
                          <template v-if="item.children">
                            <transition name="submenu-slide">
                              <div v-if="expandedParent === item.name" class="sec-sub-items">
                                <FlyoutMenuItem
                                  v-for="subitem in item.children"
                                  :key="subitem.name"
                                  :slug="subitem.type === 'route' ? subitem.name : ''"
                                  :name="subitem.name"
                                  :label="subitem.label"
                                  :class="{ active: isChildActive(subitem) }"
                                  @click="handleChildClick(subitem, close)"
                                  :active="isChildActive(subitem)"
                                  :parent="!!subitem.children"
                                />
                              </div>
                            </transition>
                          </template>
                        </FlyoutMenuItem>
                      </div>
                    </FlyoutMenu>
                  </slot>
                </div>
                <div v-if="$slots.footer" class="sec-flyout-footer">
                  <slot name="footer"></slot>
                </div>
              </div>
            </div>
          </PopoverPanel>
        </transition-slide>
      </Popover>
    </Column>
  </Section>
</template>

<style lang="scss" scoped>
.sec-mobile-navigation {
  width: 100%;
  height: v-bind(headerContainerHeight);
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.5s ease;
  position: relative;
  z-index: 5;

  :deep(.btn-flyout-trigger) {
    border: 0;
    background: transparent;
    outline: 1px;
  }
  .sec-response-toggle {
    display: flex;
    align-items: center;
  }
  .sec-branding {
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }
  &.fixed {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: auto !important;
    z-index: 100 !important;
    height: v-bind(headerContainerHeightFixed);
    background: white;
    @apply shadow-sm;
  }
}

.sec-flyout-menu {
  background: white;
  position: fixed;
  top: 0;
  width: 92%;
  left: 0;
  height: 100%;
  z-index: 20;
  @include mq($from: mlx) {
    max-width: 400px;
  }
  @apply shadow-lg;
  
  .sec-flyout-menu-inner {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    
    .btn-close {
      position: absolute;
      top: 0;
      right: 0;
      width: 50px !important;
      height: 50px !important;
      z-index: 20;
      font-size: fs(2);
      color: var(--base-650);
      display: flex;
      align-items: center;
      justify-content: center;
      border: 0;
      background: transparent;
      @include mq($from: txs) {
        display: none;
      }
    }
    
    .sec-navigation-wrapper {
      width: 100%;
      display: grid;
      gap: gr(1);
      
      > .sec-flyout-header {
        > .sec-logo {
          padding-top: gr(1);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          > img {
            transition: all 0.2s ease;
            height: 50px;
          }
        }
      }
      
      > .sec-flyout-container {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;

        .flyout-menu-item {
          
          :deep(>button.label) {
            transition: all 0 ease;
            border: 0;
            background: transparent;
            > span {
              color: var(--base-600);
            }
            &:active {
              // background: var(--base-50);
            }
          }
          &.active {
            :deep(>button.label) {
              font-weight: 500;
              border-radius: gr(0.2);
              color: var(--c-sky-blue);
              > span {
                color: var(--c-sky-blue);
              }
            }
          }
        }
      }
      
      > .sec-flyout-footer {
      }
    }
  }
}

/* New transition for submenu items */
.submenu-slide-enter-active,
.submenu-slide-leave-active {
  transition: all 0.3s ease;
}
.submenu-slide-enter-from,
.submenu-slide-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}
.submenu-slide-enter-to,
.submenu-slide-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.sec-flyout-popover {
  @include mq($from: d) {
    display: none;
  }
  .sec-mobile-nav-trigger {
    position: relative;
    align-items: center;
    @include mq($from: ds) {
      display: none;
    }
  }
}

.active > a,
.active > button {
  font-weight: bold;
  color: #007bff;
}
</style>